// Localização: frontend/src/components/Financeiro/GraficoVencamentos.jsx
// Gráfico de Vencimentos - Timeline de Contas

import React, { useState } from 'react';
import { 
    BarChart, 
    Bar, 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer, 
    Cell 
} from 'recharts';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';
import { Calendar, AlertTriangle, Clock, BarChart3, LineChart as LineChartIcon } from 'lucide-react';

/**
 * @component GraficoVencimentos
 * @description Gráfico moderno mostrando distribuição de contas por status de vencimento
 * @param {Object} resumo - Dados do resumo financeiro
 * @param {boolean} isLoading - Estado de carregamento
 * @param {string} error - Mensagem de erro
 */
const GraficoVencimentos = ({ resumo, isLoading = false, error = null }) => {
    const [viewType, setViewType] = useState('barras'); // 'barras' ou 'linha'

    if (isLoading) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
                <CardContent className="pt-6 pb-6 px-6 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                        <Calendar className="w-6 h-6 text-indigo-600" />
                        <h3 className="text-xl font-bold text-gray-800">Timeline de Vencimentos</h3>
                    </div>
                    <div className="py-12">
                        <LoadingIndicator message="Carregando dados de vencimentos..." />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
                <CardContent className="pt-6 pb-6 px-6 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                        <Calendar className="w-6 h-6 text-indigo-600" />
                        <h3 className="text-xl font-bold text-gray-800">Timeline de Vencimentos</h3>
                    </div>
                    <div className="py-4">
                        <Alert type="error" message={error} />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!resumo) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
                <CardContent className="pt-6 pb-6 px-6 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                        <Calendar className="w-6 h-6 text-indigo-600" />
                        <h3 className="text-xl font-bold text-gray-800">Timeline de Vencimentos</h3>
                    </div>
                    <div className="h-96 flex flex-col items-center justify-center py-8">
                        <Calendar className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-gray-500 text-lg">Sem dados para exibir</p>
                        <p className="text-gray-400 text-sm mt-2">Não há dados de vencimentos disponíveis</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Preparar dados para o gráfico
    const chartData = [
        {
            periodo: 'Vencidas',
            quantidade: resumo.contas_vencidas || 0,
            receitas: resumo.contas_vencidas_receitas || 0,
            despesas: resumo.contas_vencidas_despesas || 0,
            cor: '#ef4444', // red-500
            corGradiente: '#dc2626'
        },
        {
            periodo: 'Próximos 7 dias',
            quantidade: resumo.contas_a_vencer_7_dias || 0,
            receitas: resumo.contas_a_vencer_7_dias_receitas || 0,
            despesas: resumo.contas_a_vencer_7_dias_despesas || 0,
            cor: '#f59e0b', // amber-500
            corGradiente: '#d97706'
        },
        {
            periodo: 'Próximos 30 dias',
            quantidade: resumo.contas_a_vencer_30_dias || 0,
            receitas: resumo.contas_a_vencer_30_dias_receitas || 0,
            despesas: resumo.contas_a_vencer_30_dias_despesas || 0,
            cor: '#3b82f6', // blue-500
            corGradiente: '#2563eb'
        }
    ];

    const totalContas = chartData.reduce((sum, item) => sum + item.quantidade, 0);

    // Tooltip customizado moderno
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const quantidade = data.quantidade || 0;
            const receitas = data.receitas || 0;
            const despesas = data.despesas || 0;
            const porcentagem = totalContas > 0 ? ((quantidade / totalContas) * 100).toFixed(1) : 0;

            return (
                <div className="bg-white p-4 border border-gray-200 shadow-xl rounded-lg text-sm backdrop-blur-sm z-[9999] relative">
                    <p className="font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">{label}</p>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-gray-600">Total:</span>
                            <span className="font-semibold text-gray-800">{quantidade} contas</span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200 space-y-2">
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-gray-600 flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                    Receitas:
                                </span>
                                <span className="font-semibold text-green-700">{receitas} contas</span>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-gray-600 flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                    Despesas:
                                </span>
                                <span className="font-semibold text-red-700">{despesas} contas</span>
                            </div>
                        </div>
                        {totalContas > 0 && (
                            <div className="pt-2 mt-2 border-t border-gray-200">
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-600">Percentual:</span>
                                    <span className="font-semibold text-gray-700">{porcentagem}% do total</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    if (totalContas === 0) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
                <CardContent className="pt-6 pb-6 px-6 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">Timeline de Vencimentos</h3>
                                <p className="text-sm text-gray-500">
                                    Distribuição de receitas e despesas por status de vencimento
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="h-96 flex flex-col items-center justify-center py-8">
                        <Clock className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-gray-500 text-lg">Nenhuma conta pendente</p>
                        <p className="text-gray-400 text-sm mt-2">Não há contas vencidas ou a vencer no momento</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
            <CardContent className="pt-6 pb-3 px-6 h-full flex flex-col">
                {/* Header com Toggle */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                            <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-800">Timeline de Vencimentos</h3>
                            <p className="text-sm text-gray-500">
                                Distribuição de receitas e despesas por status de vencimento
                            </p>
                        </div>
                    </div>
                    
                    {/* Toggle de Visualização */}
                    <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setViewType('barras')}
                            className={`px-4 py-2 text-sm rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
                                viewType === 'barras'
                                    ? 'bg-white text-indigo-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            <BarChart3 className="w-4 h-4" />
                            Barras
                        </button>
                        <button
                            onClick={() => setViewType('linha')}
                            className={`px-4 py-2 text-sm rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
                                viewType === 'linha'
                                    ? 'bg-white text-indigo-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            <LineChartIcon className="w-4 h-4" />
                            Linha
                        </button>
                    </div>
                </div>

                {/* Cards de Resumo */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    {chartData.map((item, index) => {
                        const Icon = index === 0 ? AlertTriangle : Clock;
                        const bgGradient = index === 0 
                            ? 'from-red-50 to-red-100 border-red-200'
                            : index === 1 
                            ? 'from-amber-50 to-amber-100 border-amber-200'
                            : 'from-blue-50 to-blue-100 border-blue-200';
                        const textColor = index === 0 
                            ? 'text-red-600' 
                            : index === 1 
                            ? 'text-amber-600' 
                            : 'text-blue-600';
                        const textColorLabel = index === 0 
                            ? 'text-red-700' 
                            : index === 1 
                            ? 'text-amber-700' 
                            : 'text-blue-700';
                        
                        return (
                            <div
                                key={index}
                                className={`bg-gradient-to-br rounded-lg p-4 border ${bgGradient}`}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Icon className={`w-4 h-4 ${textColor}`} />
                                    <p className={`text-xs font-semibold ${textColorLabel}`}>
                                        {item.periodo}
                                    </p>
                                </div>
                                <p className={`text-xl font-bold ${textColor}`}>
                                    {item.quantidade}
                                </p>
                                <div className="flex items-center gap-3 mt-2 text-xs">
                                    <div className="flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                        <span className={textColorLabel}>{item.receitas || 0}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                        <span className={textColorLabel}>{item.despesas || 0}</span>
                                    </div>
                                </div>
                                {totalContas > 0 && (
                                    <p className={`text-xs mt-1 ${textColorLabel} opacity-75`}>
                                        {((item.quantidade / totalContas) * 100).toFixed(1)}% do total
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Gráfico */}
                <div className="outline-none flex-1" style={{ outline: 'none' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {viewType === 'barras' ? (
                            <BarChart 
                                data={chartData} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                            >
                                <defs>
                                    <linearGradient id="colorVencidas" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#dc2626" stopOpacity={0.8} />
                                    </linearGradient>
                                    <linearGradient id="color7Dias" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#f59e0b" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#d97706" stopOpacity={0.8} />
                                    </linearGradient>
                                    <linearGradient id="color30Dias" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#2563eb" stopOpacity={0.8} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke="#e5e7eb" 
                                    vertical={false}
                                />
                                <XAxis 
                                    dataKey="periodo" 
                                    stroke="#6b7280"
                                    style={{ fontSize: '12px' }}
                                    angle={-30}
                                    textAnchor="end"
                                    height={80}
                                    tick={{ fill: '#6b7280' }}
                                />
                                <YAxis 
                                    stroke="#6b7280"
                                    tick={{ fill: '#6b7280' }}
                                    label={{ value: 'Quantidade de Contas', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280' } }}
                                />
                                <Tooltip 
                                    content={<CustomTooltip />}
                                    wrapperStyle={{ zIndex: 9999 }}
                                />
                                <Legend 
                                    wrapperStyle={{ paddingTop: '20px', zIndex: 1 }}
                                    iconType="circle"
                                />
                                <Bar 
                                    dataKey="quantidade" 
                                    name="Contas"
                                    radius={[8, 8, 0, 0]}
                                    animationDuration={800}
                                >
                                    {chartData.map((entry, index) => {
                                        const gradientId = index === 0 
                                            ? 'colorVencidas' 
                                            : index === 1 
                                            ? 'color7Dias' 
                                            : 'color30Dias';
                                        return (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={`url(#${gradientId})`}
                                            />
                                        );
                                    })}
                                </Bar>
                            </BarChart>
                        ) : (
                            <LineChart 
                                data={chartData} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                            >
                                <defs>
                                    <linearGradient id="gradientVencidas" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gradient7Dias" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gradient30Dias" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke="#e5e7eb" 
                                    vertical={false}
                                />
                                <XAxis 
                                    dataKey="periodo" 
                                    stroke="#6b7280"
                                    style={{ fontSize: '12px' }}
                                    angle={-30}
                                    textAnchor="end"
                                    height={80}
                                    tick={{ fill: '#6b7280' }}
                                />
                                <YAxis 
                                    stroke="#6b7280"
                                    tick={{ fill: '#6b7280' }}
                                    label={{ value: 'Quantidade de Contas', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280' } }}
                                />
                                <Tooltip 
                                    content={<CustomTooltip />}
                                    wrapperStyle={{ zIndex: 9999 }}
                                />
                                <Legend 
                                    wrapperStyle={{ paddingTop: '20px', zIndex: 1 }}
                                    iconType="circle"
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="quantidade" 
                                    stroke="#ef4444" 
                                    strokeWidth={3}
                                    name="Contas"
                                    dot={{ fill: '#ef4444', r: 5, strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 7, strokeWidth: 2, stroke: '#fff' }}
                                    animationDuration={800}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default GraficoVencimentos;

